import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 } from 'uuid';
import { Button, Divider, Space } from 'antd';

import { useDebounce, useDefaultDeleteModal, useDisclosure } from '@/hooks';

import { useDeletePart } from '@/core/domain/operational/Parts/Parts.hooks';
import {
  ListStockItemsQuery,
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';

import { PageProps } from '@/types/PageProps';

import {
  AppPageHeader,
  InfiniteTable,
  ColumnsType,
  DefaultRouteTabs,
} from '@/components/_common';

import PartsExtraFilters from '@/components/Operational/Inventory/Parts/PartsExtraFilters';
import usePartsColumns from '@/components/Operational/Inventory/Parts/usePartsColumns';
import PartReportModal from '@/components/Operational/Inventory/Parts/PartReportModal';
import { getTableScrollX } from '@/utils/table';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';

import CopyIcon from '@/components/_common/CopyIcon';
import Authorities from '@/types/Authority';
import { getUserFromLocalStorage } from '@/utils/user';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import PartsChangePricesModal from '@/components/Operational/Inventory/Parts/PartsChangePricesModal';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import partsOrderColumns from '@/utils/functions/partsOrderColumns/partsOrderColumns';

function PartsPage({ route }: PageProps) {
  const intl = useIntl();
  const { handleDelete } = useDefaultDeleteModal('parts');

  const { columns, isFetching: isFetchingColumns } = usePartsColumns();
  const { companyIds } = getUserFromLocalStorage();
  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    itemType: StockItemType.Part,
    order: 'ASC',
    active: true,
    companiesIds: companyIds ? [companyIds] : undefined,
    isPartReplacement: true,
    ...convertParams<ListStockItemsQuery>({
      search: 'string',
      barcode: 'string',
      manufacturingCode: 'string',
      sku: 'string',
      companyCode: 'string',
      description: 'string',
      manufacturer: 'number',
      subGroup: 'number',
      active: 'boolean',
      location: 'string',
      isPartReplacement: 'boolean',
      prioritizePrefixMatch: 'boolean',
      availableStock: 'boolean',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
    useStockItems(true, queryDebounce);

  usePersistSearchParams(queryDebounce);

  const deleteMutation = useDeletePart();

  const parts = flatInfiniteResult(data);

  const [modalReportVisible, setModalReportVisible] = useDisclosure(false);
  const [partsChangePrices, partsChangePricesActions] = useDisclosure(false);

  const columnsWithActions: ColumnsType<StockItem> = [
    ...columns,
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      dataIndex: 'actions',
      key: 'actions',
      width: 130,
      align: 'right',
      fixed: 'right',
      render(_: unknown, record: StockItem) {
        return (
          <Space>
            <EditIcon
              redirect={`/app/operational/inventory/parts/${record.partId}`}
              disabled={
                !hasPermissions([
                  Authorities.REGISTER_PART_VIEW,
                  Authorities.REGISTER_PART_EDIT,
                ])
              }
            />

            <Divider type="vertical" />

            <CopyIcon
              redirect={`/app/operational/inventory/parts/create?partId=${record.partId}`}
              disabled={!hasPermissions([Authorities.REGISTER_PART_DUPLICATE])}
            />

            <Divider type="vertical" />

            <DeleteIcon
              disabled={!hasPermissions([Authorities.REGISTER_PART_DELETE])}
              onClick={() =>
                handleDelete(
                  () => deleteMutation.mutateAsync(record.partId || 0),
                  () => refetch(),
                )
              }
            />
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        persistSearchParams={false}
        extra={
          <SpaceForButton>
            <Button
              block
              onClick={() =>
                window.open('/privateReports/productTags', '_blank')
              }
            >
              Gerar Etiquetas
            </Button>

            <Button
              block
              type="primary"
              onClick={partsChangePricesActions.open}
              disabled={
                !hasPermissions([Authorities.REGISTER_PART_EDIT_PRODUCT_PRICES])
              }
            >
              Alterar Preços
            </Button>

            <Button
              block
              type="primary"
              onClick={setModalReportVisible.toggle}
              disabled={
                !hasPermissions([Authorities.REGISTER_PART_PRODUCT_REPORTS])
              }
            >
              <FormattedMessage id="parts.generateReport" />
            </Button>

            <Button
              block
              type="primary"
              disabled={!hasPermissions([Authorities.REGISTER_PART_ADD])}
              id="parts-create-button"
            >
              <Link to="/app/operational/inventory/parts/create">
                <FormattedMessage id="parts.create" />
              </Link>
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          searchValue={queryParams.search}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                block
                onClick={() =>
                  setQueryParams({
                    itemType: StockItemType.Part,
                    order: 'ASC',
                    active: true,
                  })
                }
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <PartsExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<StockItem>
          columns={columnsWithActions}
          onChange={(_p, _f, sorter) => {
            if (!Array.isArray(sorter)) {
              partsOrderColumns(sorter, setQueryParams);
            }
          }}
          dataSource={parts}
          rowKey={() => v4()}
          loading={isFetching || isFetchingColumns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{
            x: getTableScrollX(columns),
          }}
        />
      </DefaultRouteTabs>

      {modalReportVisible && (
        <PartReportModal
          visible={modalReportVisible}
          handleVisible={setModalReportVisible.toggle}
        />
      )}

      <PartsChangePricesModal
        open={partsChangePrices}
        onCancel={partsChangePricesActions.close}
      />
    </>
  );
}

export default PartsPage;
